import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    HittingMonitor as NyyHittingMonitor, defaultHittingMonitorFilter, dateTranslate, number, baseEvent, hitType,
    pitchTypeDisplayName,
} from 'yankees-react-utils';
import { usePitchVideoFn, useMultiVideoFn, useOktaQuery, PlayerReport } from '../../../../data';
import ReportCard from '../../../../components/ReportCard';
import { VideoPlayerModal } from '../../../../components/VideoPlayer';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import getCleanFilter from '../../../Reports/HitterStrengths/getCleanFilters';
import useCachedFilters, { CACHED_FILTER_TYPES } from '../../../../hooks/useCachedFilters';
import StatTooltip from '../../../../components/StatTooltip';

const PitchLocation = ({ modalContainer, isLhb, batter, pitches, catchers, levelid, startDate, endDate, vsteamid, teams, pitcher, isNyy = false, szTop, szBottom }) => {
    let levelAveragesAbortController = null;
    const { PlayerID } = usePlayerSelector();
    const { t: translate } = useTranslation();
    const pitchVideoFn = usePitchVideoFn();
    const multiVideoFn = useMultiVideoFn();
    const { getCachedFilters, updateCachedFilters } = useCachedFilters({
        type: CACHED_FILTER_TYPES.gamePrep,
        playerId: PlayerID,
        defaultFilters: defaultHittingMonitorFilter,
    });

    const [pitchLocationFilter, setPitchLocationFilter] = useState({
        ...getCachedFilters(),
        batterHand: isLhb ? 2 : 1,
        pitcherHand: pitcher.Throws,
    });

    const innerSetFilter = (newFilters) => {
        setPitchLocationFilter(newFilters);
        updateCachedFilters(newFilters);
    };

    const { refetch: getLevelAverages } = useOktaQuery(
        ['hitmonLevelAverages'],
        (t) => {
            if (levelAveragesAbortController) {
                levelAveragesAbortController.abort();
            }

            levelAveragesAbortController = new AbortController();
            return PlayerReport.hitmonLevelAverages(t, {
                yearid: null,
                levelids: [levelid],
                orgid: vsteamid ?? null,
                startdate: startDate,
                enddate: endDate,
                filters: JSON.stringify(getCleanFilter(pitchLocationFilter, [])),
                PlayerID,
            }, { signal: levelAveragesAbortController.signal });
        },
        {
            enabled: false,
        },
    );

    const handleGetLevelAverages = async () => {
        const { data } = await getLevelAverages();
        return data;
    };

    const tooltipFormatter = (series, point) => {
        const pitch = pitches.find((p) => p.gameid === point.gameid && p.eventseq === point.eventseq && p.pitchseq === point.pitchseq);

        const result = pitch.resultid;
        const catcher = catchers.find((c) => c.catcherid === pitch.catcherid);
        const vsteam = teams.find((t) => t.TeamID === pitch.vsteamid);

        return `${`<b>${series.name}</b><br />${`${translate('common.date')}: `}
            ${dateTranslate(translate, pitch.gamedate)}<br />${translate('sections.opponent.player.caught')}/${translate('sections.opponent.player.against')}: 
            ${catcher?.catchername}${vsteam ? `/${vsteam?.FullName}` : ''}<br />`
            + `${translate('data.pitch-type')}: ${pitchTypeDisplayName(pitch, false, false, translate)}<br />${pitch.initspeed54 ? `${translate('format.abbr-mph', { value: number(pitch.initspeed54, 0) })}<br/>` : ''
            }${pitch.istop ? `${translate('baseball.abbr-top')} ` : `${translate('baseball.abbr-bottom')} `}${pitch.inning}, ${pitch.outs} ${translate('baseball.outs', { count: pitch.outs })}, ${pitch.balls}-${pitch.strikes
            }<br />${translate('baseball.pitcher_one')}: ${pitcher.FullName} (${pitch.islhp ? 'L' : 'R'})`}${pitch.islastpitch && (result === 14 || result === 15)
                ? `<br />${hitType(pitch.hittype, false, translate)} ${baseEvent(pitch.baseevent, undefined, undefined, translate)}`
                + `${!isNyy && pitch.bip ? `, ${translate('sections.opponent.player.exit-velo')} ${pitch.exitvelo != null ? number(pitch.exitvelo, 1) : '--'}<br/>` : ''}`
                + `${isNyy && pitch.bip ? `, ${translate('sections.opponent.player.exit-velo')} ${pitch.exitveloimputed != null ? number(pitch.exitveloimputed, 1) : '--'}<br/>` : ''}`
                : ''}`;
    };
    return (
        <ReportCard
            title={`${translate('sections.opponent.player.pitch-location')} ${translate('baseball.abbr-versus')} 
            ${isLhb ? translate('baseball.lhh') : translate('baseball.rhh')}`}
        >
            <NyyHittingMonitor
                isLoading={false}
                liveFilter={pitchLocationFilter}
                setLiveFilter={innerSetFilter}
                pitches={pitches}
                pitchVideoFn={pitchVideoFn}
                multiVideoFn={multiVideoFn}
                videoPlayerModal={VideoPlayerModal}
                modalContainer={modalContainer}
                hidePerspectiveToggle
                hideEvLaChart
                hideBatterHand
                hideSprayChart
                hideLastPAs
                hideHitterTimingChart
                hideHitterTimingViewOption
                hideHitterTimingFilter
                player={batter}
                tooltipFormatter={tooltipFormatter}
                enablePlayAllInStatsTable
                useImputedData={isNyy}
                showBatTrackingStatsTablePlayerPortal
                hideGbPercent
                hideSwingDecision
                hideWhips
                enableLevelAverages={!window.$IsDugoutApp}
                getLevelAverages={handleGetLevelAverages}
                levelAveragesAbortController={levelAveragesAbortController}
                levels={levelid ? [levelid] : []}
                statTooltip={StatTooltip}
                szTop={szTop}
                szBottom={szBottom}
                vsMePlayerId={PlayerID}
            />
        </ReportCard>
    );
};

export default PitchLocation;
